import { useCallback, useEffect, useRef, useState } from "react";

/** Where a copy stands: nothing yet, it landed, or it did not. */
export type ClipboardCopyState = "idle" | "copied" | "failed";

/**
 * How long "Copied" (or the failure) stays before the control returns to its
 * resting label.
 *
 * Long enough to be read, short enough that the control is not stuck in a
 * state that no longer describes what pressing it would do.
 */
export const COPY_FEEDBACK_MS = 2_000;

export interface ClipboardCopy {
  readonly state: ClipboardCopyState;
  /** Writes `text` to the clipboard and reports the outcome in `state`. */
  readonly copy: (text: string) => Promise<void>;
}

/**
 * The ONE clipboard write in the design system (codeblock). CodeBlock and
 * CopyErrorDetailsButton both copy through it, so the presence check, the
 * failure report and the reset timing cannot drift between them.
 *
 * Data-blind: `text` is opaque, never parsed, inspected or filtered. The
 * component that calls this owns the words it shows and announces.
 */
export function useClipboardCopy(): ClipboardCopy {
  const [state, setState] = useState<ClipboardCopyState>("idle");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current !== null) clearTimeout(resetTimer.current);
    },
    [],
  );

  const copy = useCallback(async (text: string): Promise<void> => {
    if (resetTimer.current !== null) clearTimeout(resetTimer.current);
    try {
      /*
       * PRESENCE is checked, not optional-chained.
       *
       * The Clipboard API is absent on an insecure origin and in several
       * embedded webviews, and `navigator.clipboard?.writeText(…)` there
       * evaluates to `undefined` — which awaits successfully. The reader was
       * then told "Copied" with an empty clipboard.
       */
      const clipboard = navigator.clipboard;
      if (typeof clipboard?.writeText !== "function") {
        throw new Error("clipboard unavailable");
      }
      await clipboard.writeText(text);
      setState("copied");
    } catch {
      setState("failed");
    }
    resetTimer.current = setTimeout(() => setState("idle"), COPY_FEEDBACK_MS);
  }, []);

  return { state, copy };
}
